import { PhoneNumberValue } from '@/components/form/PhoneNumberField';
import { handleBEFormError } from '@/helpers/beFormErrorHelper';
import { handleMutationResult } from '@/helpers/toastHelper';
import { useDefaultPhoneRegion } from '@/services/phone/useDefaultPhoneRegion';
import { FC, Suspense } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { SubmissionErrorBoundary } from '../../../components/errors/SubmissionErrorBoundary';
import { TsForm } from '../../../components/form/Form';
import { Button } from '../../../components/shared/button/Button';
import { TSTitleCard } from '../../../components/shared/card/Card';
import { TableLoader } from '../../../components/shared/table/TableLoader';
import { normalizePhoneNumber } from '../../../helpers/phoneHelper';
import { useFlag, useVariable } from '../../../services/backstage/BackstageProvider';
import {
  CreateEmployee,
  ReadEmployee,
  useCreateUserAndEmployeeMutation
} from '../../../services/gql/graphql.generated';
import { useLocationService } from '../../../services/location/LocationService';
import strings from '../team.translations';
import { TeamMemberAddOrEditFormContent } from '../TeamMemberAddOrEditFormContent';

interface AddSingleTeamMemberProps {
  refetchCallback: () => void;
  employee: ReadEmployee;
}

export interface TeamMemberFormFields {
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: PhoneNumberValue;
  role: string;
  sendRegistration: boolean;
  userName: string;
  locationIds?: number[];
  contractTypeId?: string;
  loopCount?: number;
}

export const TSTitleCardStyle = 'space-y-5 max-w-3xl mx-auto p-5 mb-4';

const toPayload = (
  data: TeamMemberFormFields,
  locationIds: number[],
  contractTypeId: string | undefined
): CreateEmployee => ({
  firstName: data.firstName,
  lastName: data.lastName,
  role: data.role,
  userName: uuidv4(),
  sendRegistration: data.sendRegistration,
  locationIds,
  ...(data.email && { email: data.email }),
  ...(data.phoneNumber?.number && {
    phoneNumber: normalizePhoneNumber(data.phoneNumber.number, data.phoneNumber.region)
  }),
  ...(contractTypeId && { contractTypeId })
});

export const AddSingleTeamMemberForm: FC<AddSingleTeamMemberProps> = ({ refetchCallback, employee }) => {
  const defaultRegion = useDefaultPhoneRegion();

  const methods: UseFormReturn<TeamMemberFormFields> = useForm<TeamMemberFormFields>({
    defaultValues: {
      sendRegistration: true,
      locationIds: [],
      phoneNumber: { number: '', region: defaultRegion }
    },
    shouldFocusError: true
  });

  const navigate = useNavigate();
  const goBack = () => navigate('/team');
  const goToEmployeePage = (id: string) => navigate(`/team-member/${id}`);

  const mappedLocations = useLocationService(state => state.mappedLocations);
  const selectedLocationId = useLocationService(state => state.selectedLocation!.id);
  const [{ fetching }, createUserAndEmployee] = useCreateUserAndEmployeeMutation();
  const contractTypeId = useVariable('contractTypeId');
  const locationsEditable = useFlag('devices');

  const onSubmit = async (data: TeamMemberFormFields) => {
    if (fetching) return;

    if (!data.phoneNumber?.number && !data.email) return;

    const checkedLocations = mappedLocations.filter(l => l.checked);
    const locationIds =
      locationsEditable && checkedLocations.length > 0
        ? checkedLocations.map(l => Number(l.key))
        : [selectedLocationId];

    try {
      const result = await createUserAndEmployee({ employee: toPayload(data, locationIds, contractTypeId) });

      await handleMutationResult(result, {
        successMessage: 'Team member successfully added.',
        errorMessage: 'Unable to add team member.',
        onSuccess: data => {
          refetchCallback();
          goToEmployeePage(data?.createUserAndEmployee?.identityId);
        }
      });
    } catch (ex: any) {
      if (ex.graphQLErrors?.[0]?.extensions?.code === 'USER_IDENTITY' && (data.loopCount || 0) < 2) {
        await onSubmit({ ...data, loopCount: (data.loopCount ?? 0) + 1 });
      } else {
        await handleMutationResult({ error: ex } as any, {
          errorMessage: strings.common.toastNotifications.error,
          onError: error => handleBEFormError(error, methods)
        });
      }
    }
  };

  return (
    <TsForm methods={methods} onSubmit={onSubmit}>
      <TSTitleCard title={strings.addTeamMenu.addATeamMember} className={TSTitleCardStyle}>
        <SubmissionErrorBoundary>
          <Suspense fallback={<TableLoader />}>
            <TeamMemberAddOrEditFormContent addMode={true} employee={employee} methods={methods} />
          </Suspense>
        </SubmissionErrorBoundary>
      </TSTitleCard>
      <div className="space-y-5 max-w-3xl mx-auto mb-4 pt-5 pl-5">
        <div className="flex w-full items-center justify-end gap-5">
          <Button className="w-full sm:w-auto" onClick={goBack}>
            {strings.common.cancel}
          </Button>
          <Button primary className="w-full sm:w-auto" type="submit" loading={fetching}>
            {strings.addTeamMenu.addTeamMember}
          </Button>
        </div>
      </div>
    </TsForm>
  );
};
